import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';

/**
 * Get filtered stores based on criteria
 * @param {Object} params - Query parameters
 * @param {boolean} params.includeVirtual - Include virtual stores (default: false, admin only)
 * @param {boolean} params.includeInactive - Include inactive stores (default: false)
 * @returns {Promise<Object>} Store list response
 */
export const getStoresFiltered = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.includeVirtual !== undefined) queryParams.append('includeVirtual', params.includeVirtual);
  if (params.includeInactive !== undefined) queryParams.append('includeInactive', params.includeInactive);
  
  const response = await axiosInstance.get(`/stores/get-stores-filtered?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get main store only
 * @returns {Promise<Object>} Main store details
 */
export const getMainStore = async () => {
  const response = await axiosInstance.get('/stores/main-store');
  return response.data;
};

/**
 * Get virtual stores (Admin only)
 * @returns {Promise<Object>} Virtual stores list
 */
export const getVirtualStores = async () => {
  const response = await axiosInstance.get('/stores/virtual-stores');
  return response.data;
};

/**
 * Get main store and virtual stores together
 * @returns {Promise<Object>} Object with mainStore and virtualStores
 */
export const getMainAndVirtualStores = async () => {
  const response = await axiosInstance.get('/stores/main-and-virtual-stores');
  return response.data;
};

/**
 * Get store names and IDs for the current user's company (lightweight lookup list)
 * @returns {Promise<Array>} Array of { Id, Name } store objects
 */
export const getStoreNames = async () => {
  const response = await axiosInstance.get('/stores/get-store-names');
  return response.data;
};

/**
 * Create or update a store
 * @param {Object} storeData - Store data
 * @returns {Promise<Object>} Created/updated store
 */
export const addStore = async (storeData) => {
  const response = await axiosInstance.post('/stores/add-store', storeData);
  return response.data;
};

/**
 * Update store
 * @param {Object} storeData - Store data with ID
 * @returns {Promise<Object>} Updated store
 */
export const updateStore = async (storeData) => {
  const response = await axiosInstance.put('/stores/update-store', storeData);
  return response.data;
};

/**
 * Delete store
 * @param {number} id - Store ID
 * @returns {Promise<Object>} Delete response
 */
export const deleteStore = async (id) => {
  const response = await axiosInstance.delete(`/stores/delete-store/${id}`);
  return response.data;
};

/**
 * Get store by ID
 * @param {number} id - Store ID
 * @returns {Promise<Object>} Store details
 */
export const getStoreById = async (id) => {
  const response = await axiosInstance.get(`/stores/get-store/${id}`);
  return response.data;
};
